import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CarrierChargeTypesService, CarrierChargeType } from '../../../../../services/forwarding/carrier-charge-types.service';
import { CarrierChargesService, CarrierCharge } from '../../../../../services/forwarding/carrier-charges.service';
import { CarrierService } from '../../../../../services/forwarding/carrier.service';
import { Carrier } from '../../../../../models/forwarding/carrier.model';
import { PortService } from '../../../../../services/forwarding/port.service';
import { Port } from '../../../../../models/forwarding/port.model';

@Component({
  selector: 'app-carrier-charges-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './carrier-charges-tab.html',
  styleUrl: './carrier-charges-tab.css'
})
export class CarrierChargesTabComponent implements OnInit, OnDestroy {
  showAddModal = false;
  isEditMode = false;
  editingId: number | null = null;

  // Form data
  formData: Partial<CarrierCharge> = this.getEmptyFormData();

  // Data
  carrierCharges: CarrierCharge[] = [];
  carriers: Carrier[] = [];
  chargeTypes: CarrierChargeType[] = [];
  ports: Port[] = [];

  private subscriptions = new Subscription();

  constructor(
    private carrierChargesService: CarrierChargesService,
    private carrierService: CarrierService,
    private chargeTypesService: CarrierChargeTypesService,
    private portService: PortService
  ) {}

  ngOnInit(): void {
    console.log('[CarrierChargesTab] Component initialized');

    // Load carrier charges
    this.subscriptions.add(
      this.carrierChargesService.getCharges().subscribe(charges => {
        this.carrierCharges = charges;
        console.log('[CarrierChargesTab] Loaded carrier charges:', charges.length);
      })
    );

    // Load carriers
    this.subscriptions.add(
      this.carrierService.getCarriers().subscribe(carriers => {
        this.carriers = carriers;
      })
    );

    // Load charge types
    this.subscriptions.add(
      this.chargeTypesService.getChargeTypes().subscribe(types => {
        this.chargeTypes = types;
      })
    );

    // Load ports
    this.subscriptions.add(
      this.portService.getPorts().subscribe(ports => {
        this.ports = ports;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openAddModal(): void {
    this.isEditMode = false;
    this.editingId = null;
    this.formData = this.getEmptyFormData();
    this.showAddModal = true;
  }

  editCharge(charge: CarrierCharge): void {
    this.isEditMode = true;
    this.editingId = charge.id || null;
    this.formData = { ...charge };
    this.showAddModal = true;
  }

  deleteCharge(charge: CarrierCharge): void {
    if (confirm(`Are you sure you want to delete this carrier charge?`)) {
      if (charge.id) {
        this.carrierChargesService.deleteCharge(charge.id);
      }
    }
  }

  saveCharge(): void {
    // Validate required fields
    if (!this.formData.carrierId || !this.formData.chargeTypeId || !this.formData.shipmentMode ||
        !this.formData.rate || !this.formData.currency || !this.formData.validFrom) {
      alert('Please fill in all required fields');
      return;
    }

    if (this.isEditMode && this.editingId) {
      // Update existing charge
      this.carrierChargesService.updateCharge(this.editingId, this.formData);
    } else {
      // Create new charge
      const newCharge = {
        carrierId: this.formData.carrierId!,
        chargeTypeId: this.formData.chargeTypeId!,
        shipmentMode: this.formData.shipmentMode!,
        rate: this.formData.rate!,
        currency: this.formData.currency!,
        originPortId: this.formData.originPortId,
        destinationPortId: this.formData.destinationPortId,
        validFrom: this.formData.validFrom!,
        validUntil: this.formData.validUntil,
        notes: this.formData.notes
      };
      this.carrierChargesService.addCharge(newCharge);
    }
    this.closeModal();
  }

  closeModal(): void {
    this.showAddModal = false;
    this.formData = this.getEmptyFormData();
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  getCarrierName(carrierId: number): string {
    return this.carriers.find(c => c.id === carrierId)?.name || 'Unknown';
  }

  getChargeTypeName(chargeTypeId: number): string {
    return this.chargeTypes.find(ct => ct.id === chargeTypeId)?.name || 'Unknown';
  }

  getPortName(portId?: number): string {
    if (!portId) return 'Any Port';
    const port = this.ports.find(p => p.id === portId);
    return port ? `${port.name} (${port.code})` : 'Unknown';
  }

  private getEmptyFormData(): Partial<CarrierCharge> {
    return {
      carrierId: undefined,
      chargeTypeId: undefined,
      shipmentMode: 'FCL',
      rate: 0,
      currency: 'USD',
      originPortId: undefined,
      destinationPortId: undefined,
      validFrom: new Date(),
      validUntil: undefined,
      notes: ''
    };
  }
}

